import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Menu, X, Phone, MapPin, Mail, Star, Coffee, Mountain, Waves, Footprints, Facebook, Instagram, ChevronUp, UtensilsCrossed, ArrowRight, ChevronLeft, ChevronRight, BookOpen, Volume2, VolumeX, MessageCircle, Globe } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import { Lang, langLabels, langNames } from '../i18n/translations';
import heroImage from '../assets/landing_img.jpg';
import heroImage2 from '../assets/landing_img2.jpg';
import aboutImage from '../assets/about_img_new.png';
import diningImage from '../assets/dining_img.jpg';
import roomDoubleImage from '../assets/room_double.jpg';
import roomQuadrupleImage from '../assets/room_quadruple.jpg';
import roomTripleImage from '../assets/room_triple.jpg';
import gallerySunset from '../assets/gallery_sunset.jpg';
import galleryCherryBlossom from '../assets/gallery_cherry_blossom.jpg';
import galleryBedroom from '../assets/gallery_bedroom.jpg';
import galleryFireplace from '../assets/gallery_fireplace.jpg';
import galleryEvening from '../assets/gallery_evening.jpg';
import gallerySheep from '../assets/gallery_sheep.jpg';
import galleryTerrace from '../assets/gallery_terrace.jpg';
import galleryGarden from '../assets/gallery_garden.jpg';
import gallerySsn1 from '../assets/gallery_ssn1.jpg';
import gallerySsn2 from '../assets/gallery_ssn2.jpg';
import gallerySsn3 from '../assets/gallery_ssn3.jpg';
import galleryActivity1 from '../assets/gallery_activity1.jpg';
import galleryActivity2 from '../assets/gallery_activity2.jpg';

const heroImages = [heroImage, heroImage2];

const menuPages = [
  {
    sections: [
      {
        titleKey: 'menu_sec_breakfast',
        items: [
          { name: 'Thé, café, lait, beurre beldi, miel, msemen, Baghrir, huile d\'olives, pain fait maison, œufs, jus d\'orange, soupe à l\'orge et au thym', price: '39 DH' },
        ],
      },
      {
        titleKey: 'menu_sec_starters',
        items: [
          { name: 'Salade forestière', desc: 'Laitue, tomates, oignons, concombres, cœur de palmier, pousse de soja, pousse de maïs, olives noires, huile d\'olives', price: '48 DH' },
          { name: 'Salade méditerranéenne', price: '48 DH' },
          { name: 'Salade haricots verts', price: '40 DH' },
          { name: 'Salade marocaine variées', desc: 'Carottes mecharmel, zaalouk, courgettes citronnées, Taktouka, Chou-fleur aux épices', price: '60 DH' },
          { name: 'Salade traditionnelle à la tomate', desc: 'Tomates, oignons, concombre, poivrons, à la vinaigrette d\'huile d\'olive', price: '38 DH' },
          { name: 'Harira', price: '28 DH' },
          { name: 'Soupe aux légumes', price: '38 DH' },
        ],
      },
    ],
  },
  {
    sections: [
      {
        titleKey: 'menu_sec_moroccan',
        items: [
          { name: 'Tajine de poulet aux citrons', price: '98 DH' },
          { name: 'Tajine bœuf aux pruneaux', price: '115 DH' },
          { name: 'Tajine Berbère bœuf ou poulet aux légumes', price: '100 DH' },
          { name: 'Tajine marocain boulettes de viande hachée à la tomate et aux œufs', price: '98 DH' },
          { name: 'Truite saumonée marinée aux herbes du terroir sur feu de bois', price: '120 DH' },
          { name: 'Truite de l\'Atlas au four', price: '120 DH' },
          { name: '« Seffa » traditionnelle au poulet', desc: 'Vermicelles marocain cuit à la vapeur farcie au poulet caramélisé au miel et à la cannelle', price: '90 DH' },
          { name: '« Rfissa » à la manière du Moyen Atlas', desc: 'Crêpes fines laminés et arrosées d\'une sauce aux 40 épices et au poulet', price: '98 DH' },
          { name: 'Pied de veau à la Marrakchi', desc: 'Cuisson sur feu de bois, garnit aux pois chiches — sur commande la veille', price: '90 DH' },
          { name: 'Grillades variées au choix', desc: 'Brochettes filets de viande, saucisses spéciales maison, côtelettes d\'agneaux ou brochettes de poulet', price: '120 DH' },
          { name: 'Épaule d\'agneaux au four', desc: 'Épaule confite longuement au four, parsemée d\'herbes du terroir et garnie aux légumes', price: '140 DH' },
          { name: 'Pastilla au poulet', price: '120 DH' },
          { name: 'Couscous marocain bœuf ou poulet aux légumes', price: '85 DH' },
          { name: '½ Méchoui mouton pour 10 personnes', price: '1800 DH' },
        ],
      },
    ],
  },
  {
    sections: [
      {
        titleKey: 'menu_sec_international',
        items: [
          { name: 'Gratin aux légumes à la sauce béchamel', desc: 'Pommes de terres, aubergines, courgettes, oignons, chou fleurs, petits poids, brocolis', price: '100 DH' },
          { name: 'Tagliatelles à l\'arrabiata', desc: 'Légumes croustillants à une sauce piquante à l\'huile d\'olives, tomates, aubergines, courgettes, oignons, olives', price: '110 DH' },
          { name: 'Paella espagnole aux poulets', desc: 'Pour deux personnes', price: '200 DH' },
          { name: 'Paella Royale', desc: 'Poulets, viandes, poissons, fruits de mers — pour deux personnes', price: '290 DH' },
          { name: 'Poulet à la vietnamienne', desc: 'Riz, ananas, poivrons, champignons, oignons, sauce miellée', price: '98 DH' },
        ],
      },
      {
        titleKey: 'menu_sec_kids',
        items: [
          { name: 'Spaghettis à la sauce tomates', price: '55 DH' },
          { name: 'Steak haché', price: '85 DH' },
          { name: 'Escalope de poulet', price: '75 DH' },
        ],
      },
      {
        titleKey: 'menu_sec_sides',
        items: [
          { name: 'Frites', price: '15 DH' },
          { name: 'Légumes sautés', price: '25 DH' },
        ],
      },
    ],
  },
  {
    sections: [
      {
        titleKey: 'menu_sec_desserts',
        items: [
          { name: 'Corbeille de fruits selon saison', price: '35 DH' },
          { name: 'Gâteau pâtissier du jour', price: '30 DH' },
          { name: 'Salade concombre à la fleur d\'oranger et à la cannelle', price: '25 DH' },
          { name: 'Laitue à l\'orange et à la fleur d\'oranger saupoudrée de noix', price: '25 DH' },
          { name: 'Salade de fruits', price: '28 DH' },
          { name: 'Mhallabia à la saveur marocaine', price: '28 DH' },
        ],
      },
      {
        titleKey: 'menu_sec_drinks',
        items: [
          { name: 'Sidi Ali', price: '10 DH' },
          { name: 'Coca Cola', price: '10 DH' },
          { name: 'Fanta', price: '10 DH' },
          { name: 'Sprite', price: '10 DH' },
          { name: 'Café Arabica', price: '15 DH' },
          { name: 'Café Nespresso', price: '20 DH' },
          { name: 'Jus d\'orange naturel', price: '18 DH' },
          { name: 'Jus d\'avocat', price: '20 DH' },
          { name: 'Thé à la menthe', price: '10 DH' },
        ],
      },
    ],
  },
];

const roomKeys = [
  { image: roomDoubleImage, nameKey: 'room_double', size: '18 m²', descKey: 'room_double_desc', featureKeys: ['room_f_double_bed', 'room_f_private_bath', 'room_f_garden_view'] },
  { image: roomQuadrupleImage, nameKey: 'room_quadruple', size: '26 m²', descKey: 'room_quadruple_desc', featureKeys: ['room_f_quad_beds', 'room_f_private_bath', 'room_f_mountain_view'] },
  { image: roomTripleImage, nameKey: 'room_triple', size: '20 m²', descKey: 'room_triple_desc', featureKeys: ['room_f_triple_beds', 'room_f_private_bath', 'room_f_field_view'] },
];

const activityKeys = [
  { icon: 'waves', titleKey: 'act_pool', descKey: 'act_pool_desc' },
  { icon: 'footprints', titleKey: 'act_garden', descKey: 'act_garden_desc' },
  { icon: 'star', titleKey: 'act_animals', descKey: 'act_animals_desc' },
  { icon: '8', titleKey: 'act_games', descKey: 'act_games_desc' },
  { icon: 'mountain', titleKey: 'act_hiking', descKey: 'act_hiking_desc' },
  { icon: 'coffee', titleKey: 'act_lounge', descKey: 'act_lounge_desc' },
];

const testimonials = [
  { quote: "Un accueil chaleureux et authentique, avec un personnel attentif, serviable et toujours souriant.", name: "Yassine", country: "France", initial: "Y" },
  { quote: "Le site est splendide, calme, le personnel très chaleureux, la piscine très agréable.", name: "Anonyme", country: "Belgique", initial: "A" },
  { quote: "J'ai passé un séjour inoubliable dans cette ferme d'hôtes. L'accueil a été chaleureux dès mon arrivée.", name: "Kartaf", country: "Maroc", initial: "K" },
];

const galleryCategoryKeys = ['gallery_all', 'gallery_landscape', 'gallery_garden', 'gallery_rooms', 'gallery_interior', 'gallery_farm', 'gallery_animals', 'gallery_dining', 'gallery_activities'];

const galleryItemsData = [
  { src: gallerySunset, alt: 'Coucher de soleil sur la vallée', catKey: 'gallery_landscape' },
  { src: galleryCherryBlossom, alt: 'Cerisiers en fleurs au printemps', catKey: 'gallery_garden' },
  { src: galleryBedroom, alt: 'Chambre traditionnelle berbère', catKey: 'gallery_rooms' },
  { src: galleryFireplace, alt: 'Salon cheminée en pierre', catKey: 'gallery_interior' },
  { src: galleryEvening, alt: 'La ferme illuminée au crépuscule', catKey: 'gallery_farm' },
  { src: gallerySheep, alt: 'Moutons au repos dans le jardin', catKey: 'gallery_animals' },
  { src: galleryTerrace, alt: 'Terrasse panoramique sur la vallée', catKey: 'gallery_landscape' },
  { src: galleryGarden, alt: 'Espace détente dans le jardin', catKey: 'gallery_garden' },
  { src: heroImage, alt: 'Vue panoramique du Moyen-Atlas', catKey: 'gallery_landscape' },
  { src: heroImage2, alt: 'Paysage verdoyant d\'Azrou', catKey: 'gallery_landscape' },
  { src: aboutImage, alt: 'Ferme d\'hôtes La Vallée', catKey: 'gallery_farm' },
  { src: roomDoubleImage, alt: 'Chambre Double', catKey: 'gallery_rooms' },
  { src: roomQuadrupleImage, alt: 'Chambre Quadruple', catKey: 'gallery_rooms' },
  { src: roomTripleImage, alt: 'Chambre Triple', catKey: 'gallery_rooms' },
  { src: diningImage, alt: 'Saveurs du terroir marocain', catKey: 'gallery_dining' },
  { src: gallerySsn1, alt: 'Jardin sous la neige en hiver', catKey: 'gallery_landscape' },
  { src: gallerySsn2, alt: 'La ferme sous le manteau blanc', catKey: 'gallery_farm' },
  { src: gallerySsn3, alt: 'Parc enneigé et arbres en hiver', catKey: 'gallery_landscape' },
  { src: galleryActivity1, alt: 'Tir à l\'arc dans le jardin', catKey: 'gallery_activities' },
  { src: galleryActivity2, alt: 'Partie de pétanque au soleil', catKey: 'gallery_activities' },
];

/* ─── Scroll animation hook ─── */
function useScrollReveal() {
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
    );

    const elements = document.querySelectorAll(
      '.animate-on-scroll, .animate-on-scroll-left, .animate-on-scroll-right, .animate-on-scroll-scale, .stagger-children'
    );
    elements.forEach((el) => observerRef.current?.observe(el));

    return () => observerRef.current?.disconnect();
  }, []);
}

/* ─── Activity icon mapper ─── */
function ActivityIcon({ type }: { type: string }) {
  const cls = "w-8 h-8";
  switch (type) {
    case 'waves': return <Waves className={cls} />;
    case 'footprints': return <Footprints className={cls} />;
    case 'star': return <Star className={cls} />;
    case 'mountain': return <Mountain className={cls} />;
    case 'coffee': return <Coffee className={cls} />;
    case '8': return <span className="text-2xl font-bold leading-none">8</span>;
    default: return <Star className={cls} />;
  }
}

/* ─── Decorative ornament SVG ─── */
function Ornament({ className = '' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 200 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M0 10 Q25 0 50 10 Q75 20 100 10 Q125 0 150 10 Q175 20 200 10" stroke="currentColor" strokeWidth="1" fill="none" opacity="0.4" />
      <circle cx="100" cy="10" r="3" fill="currentColor" opacity="0.5" />
      <circle cx="60" cy="10" r="1.5" fill="currentColor" opacity="0.3" />
      <circle cx="140" cy="10" r="1.5" fill="currentColor" opacity="0.3" />
    </svg>
  );
}

/* ─── Language Switcher ─── */
function LanguageSwitcher({ isScrolled }: { isScrolled: boolean }) {
  const { lang, setLang } = useLanguage();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const langs: Lang[] = ['fr', 'ar', 'en', 'es', 'it'];

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-1.5 text-[13px] font-medium uppercase tracking-wider transition-colors duration-300 px-2.5 py-1.5 rounded-full border ${
          isScrolled
            ? 'text-earth-700 border-earth-200 hover:bg-earth-100'
            : 'text-white/90 border-white/20 hover:bg-white/10'
        }`}
      >
        <Globe size={14} />
        {langLabels[lang]}
      </button>
      {open && (
        <div className="absolute top-full mt-2 right-0 bg-white rounded-xl shadow-xl border border-earth-100 py-1.5 min-w-[140px] z-[60]">
          {langs.map((l) => (
            <button
              key={l}
              onClick={() => { setLang(l); setOpen(false); }}
              className={`w-full text-left px-4 py-2 text-sm transition-colors flex items-center justify-between gap-3 ${
                lang === l ? 'bg-earth-50 text-earth-900 font-semibold' : 'text-earth-600 hover:bg-earth-50'
              }`}
            >
              <span>{langNames[l]}</span>
              <span className="text-[10px] font-bold tracking-wider text-earth-400">{langLabels[l]}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── Menu Book Modal Component ─── */
function MenuBookModal({ onClose }: { onClose: () => void }) {
  const { t } = useLanguage();
  const [currentPage, setCurrentPage] = useState(0);
  const [isFlipping, setIsFlipping] = useState(false);
  const totalPages = menuPages.length;

  const goToPage = (page: number) => {
    if (page < 0 || page > totalPages || isFlipping) return;
    setIsFlipping(true);
    setTimeout(() => {
      setCurrentPage(page);
      setIsFlipping(false);
    }, 300);
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-3 md:p-6">
      <div className="absolute inset-0 bg-earth-900/70 backdrop-blur-md" onClick={onClose} />

      <div className="relative z-10 w-full max-w-5xl">
        <button onClick={onClose}
          className="absolute -top-2 -right-2 md:top-0 md:right-0 z-50 bg-earth-800 text-white rounded-full p-2 shadow-lg hover:bg-earth-700 transition-colors">
          <X size={18} />
        </button>

        <div className={`transition-opacity duration-300 ${isFlipping ? 'opacity-0 scale-[0.98]' : 'opacity-100 scale-100'}`}
          style={{ transition: 'opacity 0.3s, transform 0.3s' }}>

          {currentPage === 0 && (
            <div className="bg-gradient-to-br from-earth-800 via-earth-900 to-forest-900 rounded-2xl shadow-2xl overflow-hidden border border-earth-600/30 mx-auto max-w-lg">
              <div className="relative px-8 py-16 md:px-12 md:py-24 text-center">
                <div className="absolute inset-4 md:inset-6 border border-earth-500/20 rounded-xl pointer-events-none" />
                <div className="absolute inset-6 md:inset-8 border border-earth-500/10 rounded-lg pointer-events-none" />

                <Ornament className="w-40 mx-auto text-earth-400 mb-8" />
                <p className="text-earth-400 text-xs uppercase tracking-[0.35em] font-medium mb-4">{t('menu_tradition')}</p>
                <h2 className="font-display text-5xl md:text-6xl font-bold text-white mb-2">{t('menu_title')}</h2>
                <p className="font-display text-xl md:text-2xl text-earth-300 italic mb-1">{t('menu_carte')}</p>
                <p className="text-earth-500 text-sm mb-8">{t('menu_year')}</p>
                <Ornament className="w-40 mx-auto text-earth-400 mb-10" />

                <div className="mb-10">
                  <p className="font-display text-lg text-earth-300 mb-1">{t('menu_farmname')}</p>
                  <p className="font-display text-2xl md:text-3xl font-bold text-white">{t('menu_farmname2')}</p>
                </div>

                <button onClick={() => goToPage(1)}
                  className="group inline-flex items-center gap-3 bg-earth-600/20 border border-earth-500/30 text-earth-200 px-8 py-3.5 rounded-full text-sm font-medium transition-all duration-300 hover:bg-earth-600/40 hover:border-earth-400/40 hover:text-white">
                  <BookOpen size={16} />
                  {t('menu_open')}
                  <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                </button>
              </div>
            </div>
          )}

          {currentPage > 0 && currentPage <= totalPages && (
            <div className="bg-[#faf6f0] rounded-2xl shadow-2xl overflow-hidden border border-earth-200/60">
              <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-px bg-earth-200/50 -translate-x-1/2 z-10" />

              <div className="bg-gradient-to-r from-earth-800 via-earth-900 to-earth-800 px-6 md:px-10 py-5 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <UtensilsCrossed size={18} className="text-earth-400" />
                  <span className="font-display text-base text-earth-200 font-semibold">{t('menu_header')}</span>
                </div>
                <span className="text-earth-500 text-xs tracking-wider uppercase">{t('menu_header_sub')}</span>
              </div>

              <div className="relative">
                <div className="absolute inset-0 opacity-[0.03] pointer-events-none"
                  style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'100\' height=\'100\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'n\'%3E%3CfeTurbulence baseFrequency=\'0.8\' /%3E%3C/filter%3E%3Crect width=\'100\' height=\'100\' filter=\'url(%23n)\' opacity=\'0.5\'/%3E%3C/svg%3E")' }} />

                <div className="grid md:grid-cols-2 min-h-[60vh] max-h-[70vh] overflow-y-auto">
                  <div className="p-6 md:p-8 lg:p-10 md:border-r border-earth-200/40">
                    {menuPages[currentPage - 1].sections.slice(0, currentPage === 3 ? 2 : 1).map((section, sIdx) => (
                      <div key={sIdx} className={sIdx > 0 ? 'mt-8' : ''}>
                        <div className="text-center mb-5">
                          <h3 className="font-display text-2xl md:text-[1.7rem] font-bold text-earth-900 mb-1">{t(section.titleKey)}</h3>
                          <Ornament className="w-28 mx-auto text-earth-400" />
                        </div>
                        <div className="space-y-3">
                          {section.items.map((item: any, iIdx: number) => (
                            <div key={iIdx} className="group">
                              <div className="flex items-baseline gap-2">
                                <span className="font-display text-[0.95rem] font-semibold text-earth-800 leading-snug">{item.name}</span>
                                <span className="flex-1 border-b border-dotted border-earth-300/60 min-w-[20px] translate-y-[-3px]" />
                                <span className="font-display text-sm font-bold text-earth-700 whitespace-nowrap">{item.price}</span>
                              </div>
                              {item.desc && (
                                <p className="text-earth-500 text-xs italic mt-0.5 ml-1 leading-relaxed">{item.desc}</p>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="p-6 md:p-8 lg:p-10 bg-[#f8f4ee]">
                    {menuPages[currentPage - 1].sections.slice(currentPage === 3 ? 2 : 1).map((section, sIdx) => (
                      <div key={sIdx} className={sIdx > 0 ? 'mt-8' : ''}>
                        <div className="text-center mb-5">
                          <h3 className="font-display text-2xl md:text-[1.7rem] font-bold text-earth-900 mb-1">{t(section.titleKey)}</h3>
                          <Ornament className="w-28 mx-auto text-earth-400" />
                        </div>
                        <div className="space-y-3">
                          {section.items.map((item: any, iIdx: number) => (
                            <div key={iIdx} className="group">
                              <div className="flex items-baseline gap-2">
                                <span className="font-display text-[0.95rem] font-semibold text-earth-800 leading-snug">{item.name}</span>
                                <span className="flex-1 border-b border-dotted border-earth-300/60 min-w-[20px] translate-y-[-3px]" />
                                <span className="font-display text-sm font-bold text-earth-700 whitespace-nowrap">{item.price}</span>
                              </div>
                              {item.desc && (
                                <p className="text-earth-500 text-xs italic mt-0.5 ml-1 leading-relaxed">{item.desc}</p>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}

                    {menuPages[currentPage - 1].sections.slice(currentPage === 3 ? 2 : 1).length === 0 && (
                      <div className="flex flex-col items-center justify-center h-full text-center py-12 opacity-60">
                        <Ornament className="w-32 mx-auto text-earth-400 mb-6" />
                        <p className="font-display text-lg italic text-earth-500">{t('menu_bonappetit')}</p>
                        <p className="text-earth-400 text-xs mt-2 tracking-wider uppercase">{t('menu_header_sub')}</p>
                        <Ornament className="w-32 mx-auto text-earth-400 mt-6" />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="bg-earth-100/60 border-t border-earth-200/40 px-6 md:px-10 py-4 flex items-center justify-between">
                <button onClick={() => goToPage(currentPage - 1)}
                  className="flex items-center gap-2 text-earth-600 hover:text-earth-800 transition-colors text-sm font-medium">
                  <ChevronLeft size={16} />
                  {currentPage === 1 ? t('menu_cover') : t('menu_prev')}
                </button>

                <div className="flex items-center gap-2">
                  {Array.from({ length: totalPages }, (_, i) => (
                    <button key={i} onClick={() => goToPage(i + 1)}
                      className={`w-2 h-2 rounded-full transition-all duration-300 ${
                        currentPage === i + 1 ? 'bg-earth-700 w-5' : 'bg-earth-300 hover:bg-earth-400'
                      }`} />
                  ))}
                </div>

                <button onClick={() => currentPage < totalPages ? goToPage(currentPage + 1) : onClose()}
                  className="flex items-center gap-2 text-earth-600 hover:text-earth-800 transition-colors text-sm font-medium">
                  {currentPage < totalPages ? t('menu_next') : t('menu_close')}
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════ */
const LandingPage: React.FC = () => {
  const { t, rtl } = useLanguage();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [showMenuModal, setShowMenuModal] = useState(false);
  const [galleryFilter, setGalleryFilter] = useState('gallery_all');
  const [isMuted, setIsMuted] = useState(false);
  const [showWelcome, setShowWelcome] = useState(true);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useScrollReveal();

  useEffect(() => {
    const timer = setTimeout(() => setShowWelcome(false), 6000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const audio = new Audio('/vivaldi-spring.mp3');
    audio.loop = true;
    audio.volume = 0.3;
    audio.muted = false;
    audioRef.current = audio;

    const playAttempt = () => {
      audio.play().catch(() => {});
    };
    playAttempt();

    const handleInteraction = () => {
      if (audio.paused) {
        audio.play().catch(() => {});
      }
      window.removeEventListener('click', handleInteraction);
      window.removeEventListener('touchstart', handleInteraction);
    };
    window.addEventListener('click', handleInteraction, { once: true });
    window.addEventListener('touchstart', handleInteraction, { once: true });

    return () => {
      audio.pause();
      audio.src = '';
      window.removeEventListener('click', handleInteraction);
      window.removeEventListener('touchstart', handleInteraction);
    };
  }, []);

  const toggleMute = useCallback(() => {
    if (audioRef.current) {
      const newMuted = !isMuted;
      audioRef.current.muted = newMuted;
      if (!newMuted && audioRef.current.paused) {
        audioRef.current.play().catch(() => {});
      }
      setIsMuted(newMuted);
    }
  }, [isMuted]);

  useEffect(() => {
    const interval = setInterval(() => setCurrentSlide((p) => (p + 1) % heroImages.length), 6000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = showMenuModal ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [showMenuModal]);

  const scrollTo = useCallback((id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setIsMenuOpen(false);
  }, []);

  /* WhatsApp room booking helper */
  const openWhatsAppForRoom = (roomName: string) => {
    const msg = encodeURIComponent(`${t('wa_room_msg')} ${roomName}.`);
    window.open(`https://wa.me/212661288174?text=${msg}`, '_blank');
  };

  const navLinks = [
    { nameKey: 'nav_home', id: 'home' },
    { nameKey: 'nav_about', id: 'about' },
    { nameKey: 'nav_rooms', id: 'rooms' },
    { nameKey: 'nav_dining', id: 'dining' },
    { nameKey: 'nav_activities', id: 'activities' },
    { nameKey: 'nav_gallery', id: 'gallery' },
    { nameKey: 'nav_contact', id: 'contact' },
  ];

  const filteredGallery = galleryFilter === 'gallery_all' ? galleryItemsData : galleryItemsData.filter((i) => i.catKey === galleryFilter);

  return (
    <div className={`font-body text-earth-800 bg-earth-50 overflow-x-hidden ${rtl ? 'rtl-layout' : ''}`} dir={rtl ? 'rtl' : 'ltr'}>
      {/* ─── NAVIGATION ─── */}
      <nav className={`fixed w-full z-50 transition-all duration-500 ${isScrolled ? 'bg-earth-50/95 backdrop-blur-lg shadow-[0_1px_0_0_rgba(0,0,0,0.06)] py-3' : 'bg-transparent py-5'}`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 flex justify-between items-center">
          <button onClick={() => scrollTo('home')} className="flex items-center gap-2">
            <span className={`font-display text-2xl font-bold tracking-wide transition-colors duration-300 ${isScrolled ? 'text-earth-800' : 'text-white'}`}>
              La Vallée
            </span>
          </button>

          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <button
                key={link.id}
                onClick={() => scrollTo(link.id)}
                className={`text-[13px] font-medium uppercase tracking-[0.14em] transition-colors duration-300 hover:text-earth-600 ${isScrolled ? 'text-earth-700' : 'text-white/90 hover:text-white'}`}
              >
                {t(link.nameKey)}
              </button>
            ))}
            <LanguageSwitcher isScrolled={isScrolled} />
          </div>

          <div className="lg:hidden flex items-center gap-3">
            <LanguageSwitcher isScrolled={isScrolled} />
            <button onClick={() => setIsMenuOpen(!isMenuOpen)}>
              {isMenuOpen
                ? <X size={24} className="text-earth-800" />
                : <Menu size={24} className={isScrolled ? 'text-earth-800' : 'text-white'} />
              }
            </button>
          </div>
        </div>
      </nav>

      <div className={`lg:hidden fixed inset-0 bg-earth-50 z-[60] flex flex-col items-center justify-center gap-8 transition-all duration-500 ${isMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}>
        <button className={`absolute top-5 ${rtl ? 'left-6' : 'right-6'}`} onClick={() => setIsMenuOpen(false)}>
          <X size={24} className="text-earth-800" />
        </button>
        {navLinks.map((link) => (
          <button
            key={link.id}
            onClick={() => scrollTo(link.id)}
            className="font-display text-2xl text-earth-800 hover:text-earth-600 transition-colors"
          >
            {t(link.nameKey)}
          </button>
        ))}
      </div>

      {/* ─── HERO ─── */}
      <section id="home" className="relative h-screen flex items-end pb-24 md:pb-32 overflow-hidden grain">
        <div className="absolute inset-0">
          {heroImages.map((img, i) => (
            <img key={i} src={img} alt={`${t('hero_location')} ${i + 1}`}
              className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-[1.5s] ease-in-out ${i === currentSlide ? 'opacity-100' : 'opacity-0'}`}
            />
          ))}
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/15 to-transparent" />
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex gap-2.5">
          {heroImages.map((_, i) => (
            <button key={i} onClick={() => setCurrentSlide(i)}
              className={`h-[3px] rounded-full transition-all duration-500 ${i === currentSlide ? 'w-8 bg-white' : 'w-4 bg-white/40'}`}
              aria-label={`${t('hero_slide')} ${i + 1}`}
            />
          ))}
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 w-full">
          <p className="text-[11px] md:text-xs uppercase tracking-[0.35em] text-earth-300 mb-5 font-medium">
            {t('hero_location')}
          </p>
          <h1 className="font-display text-5xl md:text-7xl lg:text-8xl font-bold text-white leading-[0.95] mb-6 max-w-3xl">
            {t('hero_title_1')}<br />{t('hero_title_2')}
          </h1>
          <p className="text-base md:text-lg text-white/80 max-w-xl leading-relaxed mb-10">
            {t('hero_desc')}
          </p>
          <button onClick={() => scrollTo('rooms')}
            className="group inline-flex items-center gap-3 bg-white/10 backdrop-blur-sm border border-white/25 text-white px-7 py-3.5 rounded-full text-sm font-medium transition-all duration-300 hover:bg-white hover:text-earth-900 hover:border-white">
            {t('hero_cta')}
            <ArrowRight size={16} className={`transition-transform duration-300 group-hover:translate-x-1 ${rtl ? 'rotate-180' : ''}`} />
          </button>
        </div>
      </section>

      {/* ─── ABOUT ─── */}
      <section id="about" className="py-24 md:py-32 bg-earth-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
            <div className="animate-on-scroll-left">
              <div className="relative">
                <div className="rounded-2xl overflow-hidden">
                  <img src={aboutImage} alt={t('hero_title_1') + ' ' + t('hero_title_2')} className="w-full h-auto object-cover" />
                </div>
                <div className={`absolute -bottom-6 ${rtl ? '-left-6' : '-right-6'} w-32 h-32 border-2 border-earth-300 rounded-2xl -z-10`} />
              </div>
            </div>
            <div className="animate-on-scroll-right space-y-8">
              <div>
                <p className="text-xs uppercase tracking-[0.25em] text-earth-500 font-semibold mb-3">{t('about_label')}</p>
                <h2 className="font-display text-4xl md:text-5xl font-bold text-earth-900 leading-tight">
                  {t('about_title_1')}<br />{t('about_title_2')}
                </h2>
              </div>
              <div className="deco-line" />
              <div className="space-y-5 text-earth-700 leading-relaxed">
                <p dangerouslySetInnerHTML={{ __html: t('about_p1') }} />
                <p dangerouslySetInnerHTML={{ __html: t('about_p2') }} />
                <p>{t('about_p3')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── ROOMS ─── */}
      <section id="rooms" className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="animate-on-scroll text-center mb-16 max-w-2xl mx-auto">
            <p className="text-xs uppercase tracking-[0.25em] text-earth-500 font-semibold mb-3">{t('rooms_label')}</p>
            <h2 className="font-display text-4xl md:text-5xl font-bold text-earth-900 mb-4">{t('rooms_title')}</h2>
            <div className="deco-line mx-auto" />
            <p className="mt-5 text-earth-600">
              {t('rooms_desc')}
            </p>
          </div>

          <div className="stagger-children grid md:grid-cols-3 gap-6 lg:gap-8">
            {roomKeys.map((room, i) => (
              <div key={i} className="group bg-earth-50/60 rounded-2xl overflow-hidden border border-earth-200/60 hover:border-earth-300 transition-all duration-500 hover:shadow-xl hover:-translate-y-1">
                <div className="aspect-[4/3] overflow-hidden">
                  <img src={room.image} alt={t(room.nameKey)}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
                <div className="p-6 lg:p-7">
                  <div className="flex justify-between items-start mb-3">
                    <h3 className="font-display text-2xl font-bold text-earth-900">{t(room.nameKey)}</h3>
                    <span className="text-xs font-semibold text-earth-600 bg-earth-100 px-3 py-1 rounded-full">{room.size}</span>
                  </div>
                  <p className="text-earth-600 text-sm leading-relaxed mb-5">{t(room.descKey)}</p>
                  <ul className="space-y-2 mb-6">
                    {room.featureKeys.map((fk, fi) => (
                      <li key={fi} className="flex items-center text-earth-500 text-sm">
                        <span className={`w-1.5 h-1.5 rounded-full bg-earth-400 ${rtl ? 'ml-2.5' : 'mr-2.5'} flex-shrink-0`} />
                        {t(fk)}
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={() => openWhatsAppForRoom(t(room.nameKey))}
                    className="w-full py-3 bg-green-500 text-white text-sm font-semibold rounded-lg transition-all duration-300 hover:bg-green-600 flex items-center justify-center gap-2 shadow-md hover:shadow-lg">
                    <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                    </svg>
                    {t('rooms_book')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── DINING ─── */}
      <section id="dining" className="py-24 md:py-32 bg-earth-100/60">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
            <div className={`animate-on-scroll-right ${rtl ? 'order-2 lg:order-1' : 'order-2 lg:order-2'}`}>
              <div className="relative">
                <div className="rounded-2xl overflow-hidden shadow-xl">
                  <img src={diningImage} alt={t('dining_title_1') + ' ' + t('dining_title_2')} className="w-full h-auto object-cover" />
                </div>
                <div className={`absolute -bottom-4 ${rtl ? 'right-8' : 'left-8'} bg-white rounded-xl shadow-lg px-5 py-3 flex items-center gap-3`}>
                  <span className="text-2xl font-display font-bold text-earth-800">100%</span>
                  <span className="text-earth-500 text-sm leading-tight">{t('dining_local')}<br/>{t('dining_local2')}</span>
                </div>
              </div>
            </div>
            <div className={`animate-on-scroll-left ${rtl ? 'order-1 lg:order-2' : 'order-1 lg:order-1'} space-y-8`}>
              <div>
                <div className="flex items-center gap-2 text-earth-500 mb-3">
                  <Coffee size={18} />
                  <span className="text-xs uppercase tracking-[0.25em] font-semibold">{t('dining_gastro')}</span>
                </div>
                <h2 className="font-display text-4xl md:text-5xl font-bold text-earth-900 leading-tight">
                  {t('dining_title_1')}<br />{t('dining_title_2')}
                </h2>
              </div>
              <div className="deco-line" />
              <div className="space-y-5 text-earth-700 leading-relaxed">
                <p>{t('dining_text')}</p>
              </div>
              <button onClick={() => setShowMenuModal(true)}
                className="group inline-flex items-center gap-3 bg-earth-800 text-white px-7 py-3.5 rounded-lg text-sm font-semibold transition-all duration-300 hover:bg-earth-900 shadow-md hover:shadow-lg">
                {t('dining_menu')}
                <ArrowRight size={16} className={`transition-transform duration-300 group-hover:translate-x-1 ${rtl ? 'rotate-180' : ''}`} />
              </button>
            </div>
          </div>
        </div>
      </section>

      {showMenuModal && (
        <MenuBookModal onClose={() => setShowMenuModal(false)} />
      )}

      {/* ─── ACTIVITIES ─── */}
      <section id="activities" className="py-24 md:py-32 bg-forest-900 text-white relative overflow-hidden grain">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <div className="animate-on-scroll text-center mb-16 max-w-2xl mx-auto">
            <p className="text-xs uppercase tracking-[0.25em] text-earth-400 font-semibold mb-3">{t('act_label')}</p>
            <h2 className="font-display text-4xl md:text-5xl font-bold mb-4">{t('act_title')}</h2>
            <div className="deco-line mx-auto !bg-gradient-to-r !from-earth-400 !to-transparent" />
          </div>

          <div className="stagger-children grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {activityKeys.map((act, i) => (
              <div key={i} className="group p-6 rounded-xl border border-white/10 bg-white/5 backdrop-blur-sm transition-all duration-400 hover:bg-white/10 hover:border-white/20">
                <div className="text-earth-300 mb-4 transition-colors duration-300 group-hover:text-earth-200">
                  <ActivityIcon type={act.icon} />
                </div>
                <h3 className="font-display text-xl font-bold mb-2">{t(act.titleKey)}</h3>
                <p className="text-white/60 text-sm leading-relaxed">{t(act.descKey)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── TESTIMONIALS ─── */}
      <section className="py-24 md:py-32 bg-earth-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="animate-on-scroll text-center mb-16 max-w-2xl mx-auto">
            <p className="text-xs uppercase tracking-[0.25em] text-earth-500 font-semibold mb-3">{t('testi_label')}</p>
            <h2 className="font-display text-4xl md:text-5xl font-bold text-earth-900 mb-4">{t('testi_title')}</h2>
            <div className="deco-line mx-auto" />
          </div>

          <div className="stagger-children grid md:grid-cols-3 gap-6 lg:gap-8">
            {testimonials.map((tes, i) => (
              <div key={i} className="bg-white rounded-2xl p-8 border border-earth-100 shadow-sm hover:shadow-md transition-shadow duration-300">
                <div className="flex gap-1 mb-5 text-earth-400">
                  {[...Array(5)].map((_, si) => <Star key={si} size={14} fill="currentColor" />)}
                </div>
                <p className="text-earth-700 italic leading-relaxed mb-6">"{tes.quote}"</p>
                <div className="flex items-center gap-3 pt-5 border-t border-earth-100">
                  <div className="w-10 h-10 rounded-full bg-earth-100 text-earth-600 flex items-center justify-center font-display font-bold text-lg">
                    {tes.initial}
                  </div>
                  <div>
                    <div className="font-semibold text-earth-800 text-sm">{tes.name}</div>
                    <div className="text-xs text-earth-400">{tes.country}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── GALLERY ─── */}
      <section id="gallery" className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="animate-on-scroll text-center mb-12 max-w-2xl mx-auto">
            <p className="text-xs uppercase tracking-[0.25em] text-earth-500 font-semibold mb-3">{t('gallery_label')}</p>
            <h2 className="font-display text-4xl md:text-5xl font-bold text-earth-900 mb-4">{t('gallery_title')}</h2>
            <div className="deco-line mx-auto mb-5" />
            <p className="text-earth-600">
              {t('gallery_desc')}
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-2 mb-10">
            {galleryCategoryKeys.map((catKey) => (
              <button key={catKey} onClick={() => setGalleryFilter(catKey)}
                className={`px-4 py-2 rounded-full text-xs font-semibold uppercase tracking-wider transition-all duration-300 ${
                  galleryFilter === catKey
                    ? 'bg-earth-800 text-white shadow-md'
                    : 'bg-earth-100 text-earth-500 hover:bg-earth-200 hover:text-earth-700'
                }`}>
                {t(catKey)}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
            {filteredGallery.map((item, idx) => (
              <div key={`${item.alt}-${idx}`} className="group relative rounded-xl overflow-hidden aspect-[4/3] cursor-pointer">
                <img src={item.src} alt={item.alt}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-earth-900/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-400">
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <span className="inline-block text-[10px] uppercase tracking-wider text-earth-300 font-semibold mb-1">{t(item.catKey)}</span>
                    <p className="text-white text-sm font-medium leading-snug">{item.alt}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── MAP ─── */}
      <section id="location" className="py-24 md:py-32 bg-earth-100/60">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="animate-on-scroll text-center mb-12 max-w-2xl mx-auto">
            <p className="text-xs uppercase tracking-[0.25em] text-earth-500 font-semibold mb-3">{t('map_label')}</p>
            <h2 className="font-display text-4xl md:text-5xl font-bold text-earth-900 mb-4">{t('map_title')}</h2>
            <div className="deco-line mx-auto mb-5" />
            <p className="text-earth-600">
              {t('map_desc')}
            </p>
          </div>

          <div className="animate-on-scroll-scale max-w-5xl mx-auto">
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-earth-200/60">
              <div className="w-full h-[380px] md:h-[420px]">
                <iframe
                  src="https://www.google.com/maps?q=33.46080,-5.24721&z=15&output=embed"
                  width="100%" height="100%" style={{ border: 0 }}
                  allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade"
                  title={t('map_name')}
                  className="w-full h-full"
                />
              </div>
              <div className="px-6 py-5 md:px-8 md:py-6 flex flex-col md:flex-row items-center justify-between gap-5 bg-earth-50">
                <div className="flex items-center gap-4">
                  <div className="bg-earth-800 text-white p-3 rounded-full">
                    <MapPin size={20} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-earth-800">{t('map_name')}</h4>
                    <p className="text-earth-500 text-sm">{t('map_address')}</p>
                  </div>
                </div>
                <a href="https://www.google.com/maps/search/?api=1&query=FQ63%2B962%2C+P7223"
                  target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-earth-800 hover:bg-earth-900 text-white text-sm font-semibold px-6 py-3 rounded-full transition-all duration-300 shadow-md hover:shadow-lg whitespace-nowrap">
                  <MapPin size={16} />
                  {t('map_open')}
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── CONTACT ─── */}
      <section id="contact" className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="animate-on-scroll text-center mb-16 max-w-2xl mx-auto">
            <p className="text-xs uppercase tracking-[0.25em] text-earth-500 font-semibold mb-3">{t('contact_label')}</p>
            <h2 className="font-display text-4xl md:text-5xl font-bold text-earth-900 mb-4">{t('contact_title')}</h2>
            <div className="deco-line mx-auto mb-5" />
            <p className="text-earth-600">
              {t('contact_desc')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            <div className="animate-on-scroll bg-earth-50 p-6 md:p-8 rounded-2xl border border-earth-100 text-center">
              <div className="bg-earth-100 text-earth-700 w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-5">
                <MapPin size={24} />
              </div>
              <h4 className="font-display text-lg font-bold text-earth-900 mb-2">{t('contact_address_title')}</h4>
              <p className="text-earth-500 text-sm leading-relaxed whitespace-pre-line">{t('contact_address')}</p>
            </div>

            <div className="animate-on-scroll bg-earth-50 p-6 md:p-8 rounded-2xl border border-earth-100 text-center">
              <div className="bg-earth-100 text-earth-700 w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-5">
                <Phone size={24} />
              </div>
              <h4 className="font-display text-lg font-bold text-earth-900 mb-3">{t('contact_phone')}</h4>
              <div className="space-y-2">
                <a href="tel:+212661288174" className="block text-earth-700 hover:text-earth-900 transition-colors">
                  <span className="font-semibold text-sm" dir="ltr" style={{ unicodeBidi: 'embed' }}>+212 6 61 28 81 74</span>
                  <span className="block text-earth-500 text-xs">{t('contact_reservation')}</span>
                </a>
                <a href="tel:+212661288175" className="block text-earth-700 hover:text-earth-900 transition-colors">
                  <span className="font-semibold text-sm" dir="ltr" style={{ unicodeBidi: 'embed' }}>+212 6 61 28 81 75</span>
                  <span className="block text-earth-500 text-xs">M. Othmane</span>
                </a>
              </div>
            </div>

            <div className="animate-on-scroll bg-earth-50 p-6 md:p-8 rounded-2xl border border-earth-100 text-center md:col-span-2 lg:col-span-1">
              <div className="bg-earth-100 text-earth-700 w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-5">
                <Mail size={24} />
              </div>
              <h4 className="font-display text-lg font-bold text-earth-900 mb-3">{t('contact_email')}</h4>
              <div className="space-y-1.5 mb-5">
                <a href="mailto:reservation@fermedhotelavallee.com" className="block text-earth-700 hover:text-earth-900 transition-colors text-sm font-semibold">reservation@fermedhotelavallee.com</a>
                <a href="mailto:fermedhotelavallee@gmail.com" className="block text-earth-700 hover:text-earth-900 transition-colors text-sm font-semibold">fermedhotelavallee@gmail.com</a>
              </div>
              <div className="flex justify-center gap-3 pt-2">
                <a href="https://www.facebook.com/FDLAVALLEEMAROC/?locale=fr_FR" target="_blank" rel="noopener noreferrer"
                  className="bg-earth-100 text-earth-600 p-2.5 rounded-xl hover:bg-earth-200 transition-colors border border-earth-200/60">
                  <Facebook size={18} />
                </a>
                <a href="https://www.instagram.com/fermedhotelavallee/" target="_blank" rel="noopener noreferrer"
                  className="bg-earth-100 text-earth-600 p-2.5 rounded-xl hover:bg-earth-200 transition-colors border border-earth-200/60">
                  <Instagram size={18} />
                </a>
                <a href="https://wa.me/212661288174?text=Bonjour%2C%20je%20souhaite%20v%C3%A9rifier%20la%20disponibilit%C3%A9%20et%20r%C3%A9server." target="_blank" rel="noopener noreferrer"
                  className="bg-green-50 text-green-600 p-2.5 rounded-xl hover:bg-green-100 transition-colors border border-green-200/60">
                  <MessageCircle size={18} />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── FOOTER ─── */}
      <footer className="bg-earth-900 text-earth-400 py-14">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6 mb-8">
            <div className="text-center md:text-left">
              <h3 className="font-display text-2xl font-bold text-white mb-1">{t('hero_title_1')} {t('hero_title_2')}</h3>
              <p className="text-sm text-earth-500">{t('footer_tagline')}</p>
            </div>
            <div className="flex gap-4">
              <a href="https://www.facebook.com/FDLAVALLEEMAROC/?locale=fr_FR" target="_blank" rel="noopener noreferrer"
                className="text-earth-500 hover:text-earth-300 transition-colors"><Facebook size={20} /></a>
              <a href="https://www.instagram.com/fermedhotelavallee/" target="_blank" rel="noopener noreferrer"
                className="text-earth-500 hover:text-earth-300 transition-colors"><Instagram size={20} /></a>
            </div>
          </div>
          <div className="border-t border-earth-800 pt-8 flex flex-col md:flex-row justify-between items-center text-sm gap-4">
            <p className="text-earth-500">{t('footer_rights')}</p>
            <div className="flex items-center gap-6 text-earth-500">
              <span className="flex items-center gap-1.5"><MapPin size={13} /> Azrou, Maroc</span>
              <span className="flex items-center gap-1.5"><Phone size={13} /> <span dir="ltr" style={{ unicodeBidi: 'embed' }}>+212 6 61 28 81 75</span></span>
            </div>
          </div>
          <div className="border-t border-earth-800 mt-6 pt-5 text-center">
            <p className="text-earth-600 text-xs tracking-wide">
              {t('footer_powered')}{' '}
              <a
                href="https://araignee-systems.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-earth-400 hover:text-white font-semibold transition-colors duration-300"
              >
                Araignee Systems
              </a>
            </p>
          </div>
        </div>
      </footer>

      {/* ─── WELCOME TOAST ─── */}
      <div
        className={`fixed top-24 left-1/2 -translate-x-1/2 z-[60] transition-all duration-700 ease-out ${
          showWelcome
            ? 'opacity-100 translate-y-0'
            : 'opacity-0 -translate-y-6 pointer-events-none'
        }`}
      >
        <div className="bg-white/95 backdrop-blur-lg shadow-2xl rounded-2xl px-8 py-5 border border-earth-200/60 max-w-md text-center">
          <p className="font-display text-lg md:text-xl font-bold text-earth-800 mb-1">
            {t('welcome_title')}
          </p>
          <p className="text-sm text-earth-600 leading-relaxed">
            {t('welcome_text')}
          </p>
          <button
            onClick={() => setShowWelcome(false)}
            className="mt-3 text-xs text-earth-400 hover:text-earth-600 transition-colors"
          >
            {t('welcome_close')}
          </button>
        </div>
      </div>

      {/* ─── WHATSAPP BUTTON ─── */}
      <a
        href="https://wa.me/212661288174?text=Bonjour%2C%20je%20souhaite%20v%C3%A9rifier%20la%20disponibilit%C3%A9%20et%20r%C3%A9server%20une%20chambre."
        target="_blank"
        rel="noopener noreferrer"
        className={`fixed bottom-8 ${rtl ? 'left-24' : 'right-24'} z-50 group flex items-center gap-2`}
        aria-label={t('wa_aria')}
      >
        <span className="hidden md:block text-xs font-medium text-white bg-green-600/90 backdrop-blur-sm px-3 py-1.5 rounded-full shadow-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap">
          {t('wa_label')}
        </span>
        <div className="bg-green-500 hover:bg-green-600 text-white p-3.5 rounded-full shadow-lg transition-all duration-300 hover:scale-110 hover:shadow-xl">
          <svg viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
          </svg>
        </div>
      </a>

      {/* ─── MUSIC TOGGLE ─── */}
      <button
        onClick={toggleMute}
        className={`fixed bottom-8 ${rtl ? 'right-8' : 'left-8'} z-50 group flex items-center gap-2`}
        aria-label={isMuted ? t('music_aria_on') : t('music_aria_off')}
      >
        <div className={`bg-earth-800/90 backdrop-blur-sm text-white p-3 rounded-full shadow-lg transition-all duration-300 hover:bg-earth-700 ${!isMuted ? 'ring-2 ring-earth-400/40' : ''}`}>
          {isMuted ? <VolumeX size={18} /> : <Volume2 size={18} className="animate-pulse" />}
        </div>
        <span className="hidden md:block text-xs font-medium text-earth-600 bg-white/90 backdrop-blur-sm px-3 py-1.5 rounded-full shadow-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap">
          {isMuted ? t('music_on') : t('music_off')}
        </span>
      </button>

      {/* ─── SCROLL TOP ─── */}
      <button
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className={`fixed bottom-8 ${rtl ? 'left-8' : 'right-8'} bg-earth-800 text-white p-3 rounded-full shadow-lg z-50 transition-all duration-500 hover:bg-earth-700 ${isScrolled ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'}`}
        aria-label={t('scroll_top')}
      >
        <ChevronUp size={20} />
      </button>
    </div>
  );
};

export default LandingPage;
